/**
 * Sync manager — drains the offline outbox when connectivity is restored.
 * Replays each queued op against the backend; on success removes it; on
 * failure marks with error (keeps in queue). Never double-posts: each op
 * carries a client-generated idempotency key (tmp_id) that the backend
 * stores in the transaction record to prevent duplicate inserts.
 */
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';
import {
  listOps, removeOp, markOpFailed, markOpSyncing, OP_TYPES,
} from './offlineDB';

const ENDPOINT_FOR = {
  [OP_TYPES.INWARD]:     '/transactions/inward',
  [OP_TYPES.OUTWARD]:    '/transactions/outward',
  [OP_TYPES.TRANSFER]:   '/transactions/transfer',
  [OP_TYPES.PROCESSING]: '/processing/execute',
  [OP_TYPES.ADJUSTMENT]: '/stock-matrix/adjust',
};

let inFlight = false;

export async function syncOnce({ onProgress } = {}) {
  if (inFlight) return { skipped: true };
  if (!navigator.onLine) return { skipped: true, offline: true };

  inFlight = true;
  const result = { success: 0, failed: 0, total: 0 };
  try {
    const ops = [...(await listOps('pending')), ...(await listOps('failed'))];
    // Chronological order — oldest first so stock math replays correctly
    ops.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
    result.total = ops.length;

    for (const op of ops) {
      onProgress?.({ phase: 'syncing', op });
      await markOpSyncing(op.tmp_id);

      const endpoint = ENDPOINT_FOR[op.type];
      if (!endpoint) {
        await markOpFailed(op.tmp_id, `Unknown op type: ${op.type}`);
        result.failed += 1;
        continue;
      }

      try {
        const payload = { ...op.payload, client_tmp_id: op.tmp_id };
        await axios.post(`${API_URL}${endpoint}`, payload, {
          headers: { 'X-Offline-Origin': 'true' },
        });
        await removeOp(op.tmp_id);
        result.success += 1;
        onProgress?.({ phase: 'synced', op });
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || 'Sync failed';
        await markOpFailed(op.tmp_id, msg);
        result.failed += 1;
      }
    }
  } finally {
    inFlight = false;
  }

  if (result.success > 0) {
    toast.success(`Synced ${result.success} offline ${result.success === 1 ? 'entry' : 'entries'}`);
  }
  if (result.failed > 0) {
    toast.warning(`${result.failed} offline ${result.failed === 1 ? 'entry' : 'entries'} failed to sync — will retry`);
  }
  return result;
}
